import { useEffect, useId, useMemo, useState } from "react";
import { useForm, useWatch, type DefaultValues } from "react-hook-form";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useFormSafety } from "@/lib/navigation/useFormSafety";
import { toE164OrNull } from "@/lib/phone/phone";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateProfileMutation } from "@/modules/profile/services/useUpdateProfileMutation";
import { Field, Input } from "@/shared/rd";
import { ProfileDiscardModal, ProfileSaveBar, ProfileSaveError } from "./ProfileSaveBar";

type Values = { name: string; email: string; phone: string };

/**
 * The signed-in admin's own details as row fields. Editing raises the save
 * bar; leaving with unsaved edits asks first (`useFormSafety`).
 */
export function ProfileForm({
  defaultValues,
  onSaved,
}: {
  defaultValues: { name?: string; email?: string; phone?: string };
  onSaved: (message: string) => void;
}) {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const formId = useId();
  const initialValues = useMemo<DefaultValues<Values>>(
    () => ({
      name: defaultValues.name ?? "",
      email: defaultValues.email ?? "",
      phone: defaultValues.phone ?? "",
    }),
    [defaultValues.email, defaultValues.name, defaultValues.phone],
  );
  const {
    register,
    handleSubmit,
    reset,
    control,
    setError: setFieldError,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ defaultValues: initialValues, mode: "onChange" });
  const [savedValues, setSavedValues] = useState(initialValues);
  const { mutateAsync: updateProfile } = useUpdateProfileMutation();
  useEffect(() => {
    reset(initialValues);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- default profile values can arrive after initial render
    setSavedValues(initialValues);
    setError(null);
  }, [initialValues, reset]);
  const watchedValues = useWatch({ control, name: ["name", "email", "phone"] });

  const hasChanges = useMemo(() => {
    const [name = "", email = "", phone = ""] = watchedValues;
    return (
      name !== (savedValues.name ?? "") ||
      email !== (savedValues.email ?? "") ||
      phone !== (savedValues.phone ?? "")
    );
  }, [savedValues.email, savedValues.name, savedValues.phone, watchedValues]);

  const discard = () => {
    reset(savedValues);
    setError(null);
  };
  const { promptDiscard, discardDialogState } = useFormSafety({ isDirty: hasChanges, onDiscard: discard });

  const onSubmit = handleSubmit(async (values) => {
    // The banner of an earlier failure stays (its Try again shows the spinner) until this save succeeds.
    const normalizedPhone =
      values.phone.trim().length === 0 ? undefined : (toE164OrNull(values.phone) ?? undefined);
    if (values.phone.trim().length > 0 && !normalizedPhone) {
      setFieldError("phone", { type: "validate", message: t(keys.buyer.trackingValidationPhone) }, { shouldFocus: true });
      return;
    }

    try {
      await updateProfile({ ...values, phone: normalizedPhone });
    } catch (caught) {
      setError(
        caught instanceof ApiJsonClientError ? caught.message || t(keys.errors.generic) : t(keys.errors.generic),
      );
      return;
    }

    setError(null);
    const nextValues = { ...values };
    setSavedValues(nextValues);
    reset(nextValues);
    onSaved(t("admin.toastProfileSaved"));
  });

  return (
    <>
      <form id={formId} onSubmit={onSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error ? (
          <ProfileSaveError title={t("admin.profileSaveFailed")} message={error} formId={formId} retrying={isSubmitting} />
        ) : null}
        <Field layout="row" label={t(keys.common.name)} htmlFor="profile-name" error={errors.name?.message}>
          <Input
            id="profile-name"
            placeholder={t(keys.placeholders.name)}
            invalid={Boolean(errors.name)}
            autoComplete="name"
            {...register("name")}
          />
        </Field>
        <Field layout="row" label={t(keys.common.email)} htmlFor="profile-email" error={errors.email?.message}>
          <Input
            id="profile-email"
            type="email"
            dir="ltr"
            placeholder={t(keys.placeholders.email)}
            invalid={Boolean(errors.email)}
            autoComplete="email"
            {...register("email")}
          />
        </Field>
        <Field layout="row" label={t(keys.common.phone)} htmlFor="profile-phone" error={errors.phone?.message}>
          <Input
            id="profile-phone"
            type="tel"
            dir="ltr"
            placeholder={t(keys.placeholders.phone)}
            invalid={Boolean(errors.phone)}
            autoComplete="tel"
            {...register("phone")}
          />
        </Field>
      </form>
      <ProfileSaveBar
        formId={formId}
        visible={hasChanges || isSubmitting}
        saving={isSubmitting}
        onDiscard={() => promptDiscard(discard)}
      />
      <ProfileDiscardModal {...discardDialogState} />
    </>
  );
}
